/**
 * useGameImages —— per-game 圖片：登入後 eager 把整包圖 blob 進記憶體，render 從記憶體拿（零網路 / 零過期）。
 *
 * 流程（全環境統一走簽章 blob）：
 *   1. 登入成功（拿到 token）後呼叫 `loadGameImages(gameCode)`。
 *   2. `GET /v1/images/external?game_code=` 取整包 `{tag, relative_path, access}`（簽章 5 分鐘有效）。
 *   3. 每張用 `buildImageURL` 組簽章 URL → `fetch → blob → createObjectURL`，存 `tag → blob:url`（reactive）。
 *      失敗單張 retry 1 次；再失敗記 `failed`，不擋其他。
 *   4. 元件用 `resolveImg('imgtag:<tag>')` 拿 blob URL（reactive，blob 在頁面生命週期內恆有效）。
 *   5. 換 gameId / 卸載 / 關頁 → `revokeAll()` 回收所有 objectURL。
 *
 * 注意：blob 走 `fetch()` → CDN/Bucket 需對 host origin 開 CORS。
 *
 * config.json 的 img 為單一前綴（不分 cdn/bucket）；`access.target` 不影響 domain，一律用 `IMAGES_BASE`。
 */
import { ref } from 'vue'
import { fetchImages, fetchImagesPublic } from '@/api/images'
import type { GameImage } from '@/api/images/types'
import { IMAGES_BASE, IMAGES_CANDIDATES, RACE_TIMEOUT_MS, setImagesBase } from '@/runtimeConfig'
import { IMG_TAG_SCHEME } from '@/composables/useUtils'

/** 去頭尾斜線後拼 URL。 */
const joinURL = (domain: string, path: string, query = ''): string => {
  const d = domain.replace(/\/+$/, '')
  const p = path.replace(/^\/+/, '')
  return `${d}/${p}${query}`
}

/**
 * 依 access 組最終（簽章）URL。
 *   - 無 access → joinURL(imgBase, relative_path)
 *   - 有 access → path = access.path ?? relative_path；query = access.query ?? ''；domain 一律 imgBase。
 */
export const buildImageURL = (image: GameImage, imgBase: string = IMAGES_BASE): string => {
  const access = image.access
  if (!access) return joinURL(imgBase, image.relative_path)
  return joinURL(imgBase, access.path ?? image.relative_path, access.query ?? '')
}

// ── 模組級 reactive 狀態（單例；非 pinia，避免新增 store）──────────────
/** tag → blob objectURL（簽章模式）或直連雲端 URL（直連模式）。 */
const blobMap = ref<Record<string, string>>({})
/** 已載入（或載入中）的 gameCode。 */
const loadedGame = ref<string | null>(null)
const loading = ref<boolean>(false)
/** 抓失敗（retry 後仍失敗）的 tag。 */
const failed = ref<Record<string, true>>({})
/** 圖片清單快取（preload 先打過的話，整包載入直接重用，不再打第二次 API）。 */
let listCache: { code: string; images: GameImage[] } | null = null
/** 已做過「優先載」的 gameCode（背景圖那條快速通道；見 preloadGameImagesByTags）。 */
let preloadedCode: string | null = null

/** 回收所有 objectURL，清空 map。換遊戲 / 卸載 / 關頁時呼叫。 */
export const revokeAllGameImages = (): void => {
  for (const url of Object.values(blobMap.value)) {
    try {
      URL.revokeObjectURL(url)
    } catch {
      /* noop */
    }
  }
  blobMap.value = {}
  warnedMissing.clear()
  failed.value = {}
  loadedGame.value = null
  listCache = null
  preloadedCode = null
}

/** fetch 成 blob，失敗 retry 1 次。 */
const fetchBlobOnce = async (url: string): Promise<Blob> => {
  const attempt = async (): Promise<Blob> => {
    const r = await fetch(url)
    if (!r.ok) throw new Error(`HTTP ${r.status}`)
    return r.blob()
  }
  try {
    return await attempt()
  } catch {
    // 單張 retry 1 次
    return attempt()
  }
}

/**
 * `Promise.any` 的等價實作（第一個成功就 resolve、全敗才 reject）。
 * 🛑 不用 `Promise.any` —— 需 `lib: es2021`，`vue-tsc -b` 會擋 TS2550。
 */
const firstSuccess = <T>(ps: Promise<T>[]): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    let pending = ps.length
    if (pending === 0) {
      reject(new Error('no candidate'))
      return
    }
    for (const p of ps) {
      p.then(resolve, () => {
        if (--pending === 0) reject(new Error('all candidates failed'))
      })
    }
  })

/**
 * 圖片 domain race —— **用真的一張圖探**，第一個「真的取得到圖」的候選勝出。
 *
 * 🛑 為什麼不能在開機探根目錄（`HEAD` + `no-cors`）：那只證明「有東西回應」。
 *    圖片可能分別放 GCS / 阿里雲，**其中一邊在某些地區會被 ban**；被 ban 時 ISP 常回
 *    一個 **HTTP 200 的攔截頁**，`no-cors` 探針會判它贏 → 整包圖全破。
 *
 * 所以三件事一起驗，缺一不可：
 *   1. `res.ok` —— 真的有這個資源
 *   2. `content-type` 是 `image/*` —— 擋掉回 HTML 的攔截頁（此 header 屬 CORS 安全清單，讀得到）
 *   3. 走一般 `fetch`（非 no-cors）—— 順便驗這台對本站開了 CORS，blob 管線才跑得動
 *
 * 探測用的那張圖**就是清單第一張**，成功的 blob 直接留用 → 這次請求不浪費。
 * 全掛 / 逾時 → 維持 `IMAGES_BASE`（= `images[0]`，race 前的行為）。
 * 每個 session 只跑一次（`imageBaseRace` memo）。
 */
let imageBaseRace: Promise<void> | null = null

const probeImage = async (
  base: string,
  img: GameImage,
  signal: AbortSignal,
): Promise<{ base: string; blob: Blob }> => {
  const r = await fetch(buildImageURL(img, base), { signal, cache: 'no-store' })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  if (!(r.headers.get('content-type') ?? '').toLowerCase().startsWith('image/')) {
    throw new Error('not an image (攔截頁?)')
  }
  return { base, blob: await r.blob() }
}

const raceImageBase = async (
  code: string,
  images: GameImage[],
  preferTag?: string,
): Promise<void> => {
  // 🛑 樣本優先用 `preferTag`（快速通道傳主背景）—— 贏家那次的 blob 就是背景本身，
  //    快速通道從「race + 抓 bg 兩趟」變成一趟，背景反而比 race 前更早出現。
  //    沒指定 / 找不到才退回清單第一張有 tag 的圖。
  const sample =
    (preferTag ? images.find((i) => i.tag === preferTag) : undefined) ?? images.find((i) => i.tag)
  if (!sample || IMAGES_CANDIDATES.length <= 1) return // 沒得比 → 不發請求

  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), RACE_TIMEOUT_MS)
  try {
    const won = await firstSuccess(
      IMAGES_CANDIDATES.map((base) => probeImage(base, sample, ctrl.signal)),
    )
    setImagesBase(won.base) // domain 與遊戲無關 → 換款也照用
    // 探測拿到的 blob 直接留用（省一次請求）。
    // 🛑 race 中途換款則丟棄 —— 同 runInPool 的判斷，避免把 A 款的 blob 寫進 B 款的表（洩漏 objectURL）。
    if (sample.tag && !blobMap.value[sample.tag] && (preloadedCode === code || loadedGame.value === code)) {
      blobMap.value[sample.tag] = URL.createObjectURL(won.blob)
    }
  } catch {
    // 全掛 / 逾時 → 維持 images[0]，不會比 race 前更差
  } finally {
    clearTimeout(timer)
    ctrl.abort()
  }
}

/** 同一 session 只 race 一次。 */
const ensureImageBase = (code: string, images: GameImage[], preferTag?: string): Promise<void> => {
  imageBaseRace ??= raceImageBase(code, images, preferTag)
  return imageBaseRace
}

/** 圖片清單來源（登入 = fetchImages / 回放 = fetchImagesPublic）。 */
type ImagesFetcher = (code: string) => Promise<GameImage[]>

/**
 * eager 載入該遊戲整包圖，建立 `tag → blob URL` 對照（共用管線）。
 *   同一 gameCode 已載過 → 直接 return（避免重載）。換 gameCode → 先 revoke 舊的。
 *
 * 全環境統一走簽章 blob：打一次清單 API（tag→path 只有後端知道，前端不再寫死 path），
 * 逐張 fetch → blob → `blobMap[tag] = objectURL`。清單來源由 `fetcher` 決定
 * （登入走 /v1/images/external、回放走 /v1/images/public）。
 */
/**
 * 併發池：同時最多 `limit` 個 worker 在跑，跑完一個從隊列補一個。
 *
 * 🛑 不要換回 `Promise.all(items.map(...))` —— 那是「全部同時發出」：
 * 一款遊戲 20~66 張圖在 boot 瞬間全數起飛，h2 下與 Cocos 遊戲本體的下載
 * 多工搶頻寬、http/1.1 下佔滿同網域 6 條連線。池化後變成穩定細流，
 * 總完成時間略長，但這批是背景預載，無感知差異。
 */
const runInPool = async <T>(
  items: readonly T[],
  worker: (item: T) => Promise<void>,
  limit = 6,
): Promise<void> => {
  const queue = [...items]
  await Promise.all(
    Array.from({ length: Math.min(limit, queue.length) }, async () => {
      for (let item = queue.shift(); item !== undefined; item = queue.shift()) {
        await worker(item)
      }
    }),
  )
}

const loadWith = async (
  gameCode: string | null | undefined,
  fetcher: ImagesFetcher,
): Promise<void> => {
  const code = (gameCode ?? '').trim()
  if (!code) return
  if (loadedGame.value === code) return // 已載
  // 同款已經 preload 過（背景圖快速通道）→ **不要 revoke**：背景 blob 正被畫面引用，
  // revoke 會讓已顯示的背景直接破圖。只有換款才整包回收。
  if (preloadedCode !== code) revokeAllGameImages()
  loadedGame.value = code
  loading.value = true
  try {
    // preload 打過清單就重用（同一包、簽章 5 分鐘內），沒有才打 API
    const images = listCache?.code === code ? listCache.images : await fetcher(code)
    listCache = { code, images }
    await ensureImageBase(code, images) // 🛑 先選對 domain 再抓，否則可能整包抓到攔截頁

    // 逐張 fetch 成 blob，key = tag。走併發池（同時最多 6 張），不要 Promise.all 全發。
    await runInPool(images, async (img) => {
      const tag = img.tag
      if (!tag) return
      if (blobMap.value[tag]) return // preload 已抓過（背景圖）→ 不重抓
      try {
        const blob = await fetchBlobOnce(buildImageURL(img))
        if (loadedGame.value !== code) return // 載入中途換遊戲 → 丟棄
        // 🛑 就地寫入，**不要** `blobMap.value = { ...blobMap.value, [tag]: url }`：
        //    ref 包物件本身就是 deep reactive，就地 assign 一樣會觸發依賴更新；
        //    展開整份 map 等於每張圖複製一次全表（O(n²)）+ 讓所有 consumer 的
        //    computed 全表失效重算（zcm 66 張 = 66 次全量重算）。同理 failed。
        blobMap.value[tag] = URL.createObjectURL(blob)
      } catch {
        failed.value[tag] = true
      }
    })

    // 載完摘要：抓「清單有、但 fetch 失敗」的圖。沒有失敗就不吵。
    const failedTags = Object.keys(failed.value)
    if (failedTags.length) {
      console.warn(
        `[gameImages] ${code}: ${images.length - failedTags.length}/${images.length} 張成功，` +
          `${failedTags.length} 張失敗 →`,
        failedTags,
      )
    }
  } catch (e) {
    // 整包失敗（端點未上線 / 401…）→ 不擋 app，記 warn。
    //   並把 loadedGame 還原（清掉本次標記）→ 之後重連 / 重登入可重試；
    //   「已成功載過」則 loadedGame 維持 = code，重連不會重抓整包。
    console.warn('[gameImages] load failed for', code, e)
    if (loadedGame.value === code) loadedGame.value = null
  } finally {
    if (loadedGame.value === code) loading.value = false
  }
}

/**
 * **優先載**指定 tag（目前用途：`chrome.background` 主背景）——
 * 整包彈窗圖被刻意延後到 LOAD_COMPLETE（不搶 Cocos 資源），但主背景從進遊戲那一刻
 * 就在畫面上（host 大底 / iframe 周圍），跟著延後會「一進遊戲沒背景」。
 * 這條快速通道在 authReady 後立刻抓**清單 + 這一兩張**（頻寬可忽略），
 * 之後整包載入重用同一份清單、跳過已抓的 tag，不會重打 API 也不會 revoke 掉正在顯示的 blob。
 *
 * 冪等；`loadGameImages` 已跑過（或跑完）同款 → no-op。
 */
export const preloadGameImagesByTags = async (
  gameCode: string | null | undefined,
  tags: readonly string[],
): Promise<void> => {
  const code = (gameCode ?? '').trim()
  const want = tags.filter(Boolean)
  if (!code || want.length === 0) return
  if (loadedGame.value === code) return // 整包已在載 / 已載完 → 不用快速通道
  if (preloadedCode && preloadedCode !== code) revokeAllGameImages()
  preloadedCode = code
  try {
    const images =
      listCache?.code === code ? listCache.images : await fetchImages({ gameCode: code })
    listCache = { code, images }
    // 🛑 先選對 domain 再抓，否則可能整包抓到攔截頁。
    //    樣本用 want[0]（= 主背景）→ 探測那次的 blob 就是背景，下面的 filter 會跳過它。
    await ensureImageBase(code, images, want[0])
    await runInPool(
      images.filter((img) => img.tag && want.includes(img.tag) && !blobMap.value[img.tag!]),
      async (img) => {
        try {
          const blob = await fetchBlobOnce(buildImageURL(img))
          if (preloadedCode !== code && loadedGame.value !== code) return // 中途換款 → 丟棄
          blobMap.value[img.tag!] = URL.createObjectURL(blob)
        } catch {
          failed.value[img.tag!] = true
        }
      },
    )
  } catch (e) {
    // 快速通道失敗不擋任何流程（背景走 fallback 色）；清 preloadedCode 讓整包載入照常 revoke+重來
    console.warn('[gameImages] preload failed for', code, e)
    if (preloadedCode === code) preloadedCode = null
    if (listCache?.code === code) listCache = null
  }
}

/** 登入後 eager 載入（登入 token 走 `/v1/images/external`）。 */
export const loadGameImages = (gameCode: string | null | undefined): Promise<void> =>
  loadWith(gameCode, (code) => fetchImages({ gameCode: code }))

/**
 * 回放模式 eager 載入（一次性 token 走 `/v1/images/public`，**無登入**）。
 * ResultDetailApp 進回放時呼叫，讓盤面 / WinSection 的 `resolveImg('imgtag:..')` 拿得到 blob。
 */
export const loadGameImagesPublic = (
  gameCode: string | null | undefined,
  opts: { operatorId: string },
): Promise<void> =>
  loadWith(gameCode, (code) => fetchImagesPublic({ gameCode: code, operatorId: opts.operatorId }))

/**
 * render leaf 包這層：把 assets 的 `imgByTag` sentinel（`imgtag:<tag>`）→ 可用 URL（reactive）。
 *   - 空 / 非字串 → ''；blob:/data: → 原樣。
 *   - **tag 圖**（`imgtag:` 開頭）→ 取 tag 查 blobMap：
 *       已載好 → blob URL；載入中/缺 → ''（避免破圖閃，blob 載好會 reactive pop 進來）。
 *   - 其餘（本地 bundled / 外部 URL / 舊式 imgFromCloud 殘留）→ 原樣放行。
 */
/** 已警告過的缺圖 tag（每個只吵一次，render 迴圈才不會洗版）。 */
const warnedMissing = new Set<string>()

export const resolveImg = (src: string | undefined | null): string => {
  if (!src || typeof src !== 'string') return '' // 防呆：非字串（誤傳物件等）→ 空字串
  if (src.startsWith('blob:') || src.startsWith('data:')) return src
  if (src.startsWith(IMG_TAG_SCHEME)) {
    const tag = src.slice(IMG_TAG_SCHEME.length)
    const url = blobMap.value[tag]
    if (url) return url
    /*
     * 查不到就回 '' → `<img src="">` 在瀏覽器上就是**破圖**。
     * 只有「整包已載完（loading=false）且不是 fetch 失敗」才警告 —— 那代表
     * **清單裡根本沒有這個 tag**（assets.ts 打錯字 / 圖沒上傳 / 後端漏回），
     * 這種錯 build 不會紅、TS 不會擋，不吼一聲就只能在真機上瞎找。
     */
    if (!loading.value && loadedGame.value && !failed.value[tag] && !warnedMissing.has(tag)) {
      warnedMissing.add(tag)
      console.warn(`[gameImages] 缺圖 tag（清單裡沒有）: ${tag}`)
    }
    return ''
  }
  return src // 本地 bundled / 外部 / 舊式雲端 URL → 原樣
}

export const useGameImages = () => ({
  loading,
  preloadGameImagesByTags,
  loadedGame,
  failed,
  loadGameImages,
  resolveImg,
  revokeAllGameImages,
  buildImageURL,
  loadGameImagesPublic,
})
